import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { login, sendResetPasswordEmail } from "../services/ApiService";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (event) => {
    event.preventDefault();
    setSnackbar({ ...snackbar, open: false });
    try {
      const response = await login({ email, password });
      if (response.status === 200) {
        const { token, user } = response.data;
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));
        setSnackbar({ open: true, message: "Login successful!", severity: "success" });
        setTimeout(() => navigate("/"), 1000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Invalid email or password";
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
    }
  };

  const handlePasswordReset = async () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      await sendResetPasswordEmail({ email: resetEmail });
      setSnackbar({ open: true, message: "Password reset link sent.", severity: "success" });
      setOpenDialog(false);
      setResetEmail("");
    } catch (err) {
      setSnackbar({ open: true, message: "Email not found.", severity: "error" });
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(160deg, #0b1e3d 0%, #0d2b5e 50%, #0b1e3d 100%)",
        padding: 2,
      }}
    >
      {/* Subtle background pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(96,165,250,0.06) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(96,165,250,0.04) 0%, transparent 50%)",
          pointerEvents: "none",
        }}
      />

      <Box sx={{ position: "relative", width: "100%", maxWidth: 400 }}>
        {/* Logo */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <img
            src="/banner_logo.png"
            alt="Monit CRM"
            style={{ width: 140, height: "auto", opacity: 0.95 }}
          />
        </Box>

        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 20px 48px rgba(0,0,0,0.4)",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "#fff",
          }}
        >
          <CardContent sx={{ p: 3.5 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#0b1e3d", mb: 0.5, textAlign: "center" }}
            >
              Welcome back
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", textAlign: "center", mb: 3 }}
            >
              Sign in to your account
            </Typography>

            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Password"
                variant="outlined"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{ mb: 1 }}
              />

              <Box textAlign="right" sx={{ mb: 2.5 }}>
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => setOpenDialog(true)}
                  sx={{ color: "#0d2b5e", fontWeight: 500 }}
                >
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="medium"
                sx={{ py: 1.1, fontSize: "0.9rem", fontWeight: 600 }}
              >
                Sign In
              </Button>
            </form>

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary" }}>
              Don't have an account?{" "}
              <Link
                href="/register"
                underline="hover"
                sx={{ fontWeight: 600, color: "#0d2b5e" }}
              >
                Sign up
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#0b1e3d", pb: 1 }}>
          Reset Password
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Enter your email"
            type="email"
            fullWidth
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)} variant="outlined" size="small">
            Cancel
          </Button>
          <Button onClick={handlePasswordReset} variant="contained" size="small">
            Send Reset Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
